import React, { useCallback, useEffect, useRef, useState } from "react";

interface StandaloneFetalSexBalloonPressData {
  balloonMotionRef: React.RefObject<HTMLSpanElement>;
  balloonPressHandlers: {
    onBlur: () => void;
    onContextMenu: (event: React.MouseEvent<HTMLButtonElement>) => void;
    onKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
    onKeyUp: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
    onPointerCancel: (event: React.PointerEvent<HTMLButtonElement>) => void;
    onPointerDown: (event: React.PointerEvent<HTMLButtonElement>) => void;
    onPointerUp: (event: React.PointerEvent<HTMLButtonElement>) => void;
  };
  hideResult: () => void;
  isCompletingReveal: boolean;
  isPressingBalloon: boolean;
  isResultRevealed: boolean;
}

type BalloonPressInputMethod = "keyboard" | "pointer";
type BalloonPressCancelReason =
  | "blur"
  | "key_up"
  | "page_hide"
  | "pointer_cancel"
  | "pointer_up"
  | "visibility_hidden";

interface StandaloneFetalSexBalloonPressAnalytics {
  onPressCancel?: (
    reason: BalloonPressCancelReason,
    holdDurationMs: number,
  ) => void;
  onPressStart?: (inputMethod: BalloonPressInputMethod) => void;
}

// The balloon animation integrates a "charge" value in [0, 1] each animation
// frame: holding the balloon charges it, releasing resets it, and a full charge
// pops the balloon outward until it covers the whole card.
export const BALLOON_HOLD_TOTAL_MS = 3000;
export const BALLOON_POP_MS = 300;
// The balloon element is laid out at its fully-inflated size and scaled down
// when deflated; scaling a small raster up instead would blur the SVG.
export const BALLOON_MAX_SCALE = 4.5;
const BALLOON_MIN_SCALE = 1 / BALLOON_MAX_SCALE;
const GROW_FRACTION = 1 / 3;
const SHAKE_MIN_HZ = 1.5;
const SHAKE_MAX_HZ = 8;
const SHAKE_MIN_AMP_DEG = 3;
const SHAKE_MAX_AMP_DEG = 30;
const JITTER_START_CHARGE = 0.5;
const JITTER_MAX_PX = 3;
const ROTATION_DECAY_TAU_MS = 50;
const OUTLINE_FADE_CHARGE = 0.08;
const BALLOON_BODY_COVER_FRACTION = (7.5 - 2) / 45;
const POP_COVER_MARGIN = 1.15;
const FALLBACK_POP_SCALE = 16;
// Browsers suspend animation frames in background tabs; clamp the per-frame
// elapsed time so returning to the tab does not instantly pop the balloon.
const MAX_FRAME_DT_MS = 64;

const HOLD_KEYS = new Set(["Enter", " "]);

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

const easeInOutCubic = (t: number): number =>
  t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;

const easeInCubic = (t: number): number => t ** 3;

const shakeFrequencyHz = (charge: number): number =>
  SHAKE_MIN_HZ + (SHAKE_MAX_HZ - SHAKE_MIN_HZ) * charge ** 2;

const shakeAmplitudeDeg = (charge: number): number =>
  SHAKE_MIN_AMP_DEG + (SHAKE_MAX_AMP_DEG - SHAKE_MIN_AMP_DEG) * charge ** 1.5;

interface CompleteRevealOptions {
  isCompletingRevealRef: React.MutableRefObject<boolean>;
  isMountedRef: React.MutableRefObject<boolean>;
  onRevealFailure: () => void;
  onRevealRef: React.MutableRefObject<
    (() => boolean | Promise<boolean>) | undefined
  >;
  setIsCompletingReveal: React.Dispatch<React.SetStateAction<boolean>>;
  setIsResultRevealed: React.Dispatch<React.SetStateAction<boolean>>;
}

const completeReveal = async ({
  isCompletingRevealRef,
  isMountedRef,
  onRevealFailure,
  onRevealRef,
  setIsCompletingReveal,
  setIsResultRevealed,
}: CompleteRevealOptions): Promise<void> => {
  const completingRevealRef = isCompletingRevealRef;
  completingRevealRef.current = true;
  setIsCompletingReveal(true);
  let shouldReveal = true;
  try {
    shouldReveal = (await onRevealRef.current?.()) !== false;
  } catch {
    shouldReveal = false;
  } finally {
    completingRevealRef.current = false;
    if (isMountedRef.current) {
      setIsCompletingReveal(false);
    }
  }
  if (isMountedRef.current) {
    if (shouldReveal) {
      setIsResultRevealed(true);
    } else {
      onRevealFailure();
    }
  }
};

export const useStandaloneFetalSexBalloonPress = (
  cardRef: React.RefObject<HTMLDivElement>,
  onReveal?: () => boolean | Promise<boolean>,
  analytics?: StandaloneFetalSexBalloonPressAnalytics,
): StandaloneFetalSexBalloonPressData => {
  const [isCompletingReveal, setIsCompletingReveal] = useState(false);
  const [isPressingBalloon, setIsPressingBalloon] = useState(false);
  const [isResultRevealed, setIsResultRevealed] = useState(false);
  const balloonMotionRef = useRef<HTMLSpanElement>(null);
  const circleElRef = useRef<SVGElement | null>(null);
  const chargeRef = useRef(0);
  const isPressedRef = useRef(false);
  const isPoppingRef = useRef(false);
  const isCompletingRevealRef = useRef(false);
  const isMountedRef = useRef(true);
  const lastTimestampRef = useRef<number | null>(null);
  const analyticsRef = useRef(analytics);
  const onRevealRef = useRef(onReveal);
  const pressStartedAtRef = useRef<number | null>(null);
  const popProgressRef = useRef(0);
  const popScaleRef = useRef(FALLBACK_POP_SCALE);
  const rafIdRef = useRef<number | null>(null);
  const settleRotationRef = useRef(0);
  const shakePhaseRef = useRef(0);
  analyticsRef.current = analytics;
  onRevealRef.current = onReveal;

  const stopLoop = useCallback(() => {
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    lastTimestampRef.current = null;
  }, []);

  const resetBalloon = useCallback(() => {
    stopLoop();
    isPressedRef.current = false;
    isPoppingRef.current = false;
    chargeRef.current = 0;
    popProgressRef.current = 0;
    settleRotationRef.current = 0;
    shakePhaseRef.current = 0;
    pressStartedAtRef.current = null;
    circleElRef.current = null;
  }, [stopLoop]);

  const computePopScale = useCallback(() => {
    const motionEl = balloonMotionRef.current;
    const cardEl = cardRef.current;
    if (!motionEl || !cardEl) {
      return FALLBACK_POP_SCALE;
    }

    const motionRect = motionEl.getBoundingClientRect();
    const cardRect = cardEl.getBoundingClientRect();
    const centerX = motionRect.left + motionRect.width / 2;
    const centerY = motionRect.top + motionRect.height / 2;
    const farthestCorner = Math.max(
      Math.hypot(cardRect.left - centerX, cardRect.top - centerY),
      Math.hypot(cardRect.right - centerX, cardRect.top - centerY),
      Math.hypot(cardRect.left - centerX, cardRect.bottom - centerY),
      Math.hypot(cardRect.right - centerX, cardRect.bottom - centerY),
    );
    const coverRadius = motionEl.offsetWidth * BALLOON_BODY_COVER_FRACTION;
    const scale = (farthestCorner / coverRadius) * POP_COVER_MARGIN;
    return Number.isFinite(scale) && scale > 1 ? scale : FALLBACK_POP_SCALE;
  }, [cardRef]);

  const applyPopFrame = useCallback((dt: number) => {
    const motionEl = balloonMotionRef.current;
    if (!motionEl) {
      return;
    }

    settleRotationRef.current *= Math.exp(-dt / ROTATION_DECAY_TAU_MS);
    const scale =
      1 + (popScaleRef.current - 1) * easeInCubic(popProgressRef.current);
    motionEl.style.transform = `rotate(${settleRotationRef.current.toFixed(
      2,
    )}deg) scale(${scale.toFixed(3)})`;
  }, []);

  const applyFrame = useCallback((pressed: boolean, dt: number) => {
    const motionEl = balloonMotionRef.current;
    if (!motionEl) {
      return;
    }

    const charge = chargeRef.current;
    const growT = Math.min(charge / GROW_FRACTION, 1);
    const scale =
      BALLOON_MIN_SCALE + (1 - BALLOON_MIN_SCALE) * easeInOutCubic(growT);

    let rotation = 0;
    let jitterX = 0;
    let jitterY = 0;
    if (pressed) {
      rotation = shakeAmplitudeDeg(charge) * Math.sin(shakePhaseRef.current);
      settleRotationRef.current = rotation;
      const jitterT =
        (charge - JITTER_START_CHARGE) / (1 - JITTER_START_CHARGE);
      if (jitterT > 0) {
        const jitter = JITTER_MAX_PX * jitterT ** 2;
        jitterX = (Math.random() * 2 - 1) * jitter;
        jitterY = (Math.random() * 2 - 1) * jitter;
      }
    } else {
      settleRotationRef.current *= Math.exp(-dt / ROTATION_DECAY_TAU_MS);
      if (Math.abs(settleRotationRef.current) < 0.1) {
        settleRotationRef.current = 0;
      }
      rotation = settleRotationRef.current;
    }

    motionEl.style.transform = `translate(${jitterX.toFixed(
      2,
    )}px, ${jitterY.toFixed(2)}px) rotate(${rotation.toFixed(
      2,
    )}deg) scale(${scale.toFixed(3)})`;
    if (circleElRef.current) {
      circleElRef.current.style.opacity = String(
        clamp(1 - charge / OUTLINE_FADE_CHARGE, 0, 1),
      );
    }
  }, []);

  const clearFrameStyles = useCallback(() => {
    if (balloonMotionRef.current) {
      balloonMotionRef.current.style.transform = "";
    }
    if (circleElRef.current) {
      circleElRef.current.style.opacity = "";
      circleElRef.current = null;
    }
  }, []);

  const tick = useCallback(
    (timestamp: number) => {
      const dt = Math.min(
        lastTimestampRef.current === null
          ? 0
          : timestamp - lastTimestampRef.current,
        MAX_FRAME_DT_MS,
      );
      lastTimestampRef.current = timestamp;

      if (isPoppingRef.current) {
        popProgressRef.current = Math.min(
          popProgressRef.current + dt / BALLOON_POP_MS,
          1,
        );
        if (popProgressRef.current >= 1) {
          stopLoop();
          setIsPressingBalloon(false);
          completeReveal({
            isCompletingRevealRef,
            isMountedRef,
            onRevealFailure: () => {
              clearFrameStyles();
              resetBalloon();
            },
            onRevealRef,
            setIsCompletingReveal,
            setIsResultRevealed,
          });
          return;
        }
        applyPopFrame(dt);
        rafIdRef.current = requestAnimationFrame(tick);
        return;
      }

      const pressed = isPressedRef.current;

      chargeRef.current = clamp(
        chargeRef.current + (pressed ? dt / BALLOON_HOLD_TOTAL_MS : 0),
        0,
        1,
      );

      if (pressed && chargeRef.current >= 1) {
        isPoppingRef.current = true;
        popScaleRef.current = computePopScale();
        applyPopFrame(dt);
        rafIdRef.current = requestAnimationFrame(tick);
        return;
      }

      if (pressed) {
        shakePhaseRef.current +=
          (2 * Math.PI * shakeFrequencyHz(chargeRef.current) * dt) / 1000;
      }

      applyFrame(pressed, dt);
      rafIdRef.current = requestAnimationFrame(tick);
    },
    [
      applyFrame,
      applyPopFrame,
      clearFrameStyles,
      computePopScale,
      resetBalloon,
      stopLoop,
    ],
  );

  const startPress = useCallback(
    (inputMethod: BalloonPressInputMethod) => {
      if (
        isPressedRef.current ||
        isPoppingRef.current ||
        isCompletingRevealRef.current
      ) {
        return;
      }
      isPressedRef.current = true;
      pressStartedAtRef.current = performance.now();
      setIsPressingBalloon(true);
      analyticsRef.current?.onPressStart?.(inputMethod);
      circleElRef.current =
        balloonMotionRef.current?.querySelector<SVGElement>(
          ".standalone-fetal-sex-balloon-circle",
        ) ?? null;

      if (rafIdRef.current === null) {
        lastTimestampRef.current = null;
        rafIdRef.current = requestAnimationFrame(tick);
      }
    },
    [tick],
  );

  const stopPress = useCallback(
    (reason: BalloonPressCancelReason) => {
      if (isPoppingRef.current) {
        return;
      }
      const wasPressed = isPressedRef.current;
      const holdDurationMs =
        pressStartedAtRef.current === null
          ? 0
          : Math.max(
              0,
              Math.round(performance.now() - pressStartedAtRef.current),
            );
      stopLoop();
      isPressedRef.current = false;
      pressStartedAtRef.current = null;
      chargeRef.current = 0;
      settleRotationRef.current = 0;
      shakePhaseRef.current = 0;
      clearFrameStyles();
      setIsPressingBalloon(false);
      if (wasPressed) {
        analyticsRef.current?.onPressCancel?.(reason, holdDurationMs);
      }
    },
    [clearFrameStyles, stopLoop],
  );

  const hideResult = useCallback(() => {
    resetBalloon();
    setIsPressingBalloon(false);
    setIsResultRevealed(false);
  }, [resetBalloon]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      stopLoop();
    };
  }, [stopLoop]);

  useEffect(() => {
    if (!isPressingBalloon) {
      return undefined;
    }

    const stopPressWhenHidden = (): void => {
      if (document.visibilityState === "hidden") {
        stopPress("visibility_hidden");
      }
    };
    const stopPressOnPageHide = (): void => stopPress("page_hide");

    document.addEventListener("visibilitychange", stopPressWhenHidden);
    window.addEventListener("pagehide", stopPressOnPageHide);

    return () => {
      document.removeEventListener("visibilitychange", stopPressWhenHidden);
      window.removeEventListener("pagehide", stopPressOnPageHide);
    };
  }, [isPressingBalloon, stopPress]);

  const startPointerPress = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      if (event.button > 0) {
        return;
      }
      event.currentTarget.setPointerCapture?.(event.pointerId);
      startPress("pointer");
    },
    [startPress],
  );

  const stopPointerPress = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
        event.currentTarget.releasePointerCapture?.(event.pointerId);
      }
      stopPress("pointer_up");
    },
    [stopPress],
  );

  const cancelPointerPress = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
        event.currentTarget.releasePointerCapture?.(event.pointerId);
      }
      stopPress("pointer_cancel");
    },
    [stopPress],
  );

  const startKeyboardPress = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (!HOLD_KEYS.has(event.key)) {
        return;
      }
      event.preventDefault();
      if (!event.repeat) {
        startPress("keyboard");
      }
    },
    [startPress],
  );

  const stopKeyboardPress = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (!HOLD_KEYS.has(event.key)) {
        return;
      }
      event.preventDefault();
      stopPress("key_up");
    },
    [stopPress],
  );

  const suppressContextMenu = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
    },
    [],
  );

  return {
    balloonMotionRef,
    balloonPressHandlers: {
      onBlur: () => stopPress("blur"),
      onContextMenu: suppressContextMenu,
      onKeyDown: startKeyboardPress,
      onKeyUp: stopKeyboardPress,
      onPointerCancel: cancelPointerPress,
      onPointerDown: startPointerPress,
      onPointerUp: stopPointerPress,
    },
    hideResult,
    isCompletingReveal,
    isPressingBalloon,
    isResultRevealed,
  };
};
